import styled from "@emotion/styled";
import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Info } from "@livechat/design-system-icons";
import { DesignToken, Icon, RadiusToken, SpacingToken, Text } from "@livechat/design-system-react-components";

import { CannedResponseContent } from "components/canned-response-content/CannedResponseContent";
import Flex from "components/layout/Flex";
import { selectCannedResponses, selectCannedResponsesArray } from "store/entities/canned-responses";
import {
  IRuleActionMessage,
  IRuleActionSendFollowUp,
  IRuleActionSendRatingRequest,
} from "store/entities/rules/interfaces";
import { withVar } from "themes/helpers";

import { validCannedResponse } from "../validators";

import { ActionFieldArrayElement } from "./interfaces";
import { isActionType } from "./utils";

interface IProps {
  input: FieldInputProps<ActionFieldArrayElement>;
  onAccordionOpen: () => void;
}

export const ActionMultilineLabel: FC<IProps> = ({ input, onAccordionOpen }) => {
  const cannedResponses = useSelector(selectCannedResponses);
  const allCannedResponses = useSelector(selectCannedResponsesArray);

  if (
    isActionType<IRuleActionSendRatingRequest>(input, "sendRatingRequest") ||
    isActionType<IRuleActionSendFollowUp>(input, "sendFollowUp") ||
    isActionType<IRuleActionMessage>(input, "message")
  ) {
    if (!input.value.template) {
      const invalidCanned = validCannedResponse(input.value.cannedResponseID, allCannedResponses);
      const cannedResponse = input.value.cannedResponseID && cannedResponses[input.value.cannedResponseID];
      return (
        <MessageContainer error={Boolean(invalidCanned)} onClick={onAccordionOpen}>
          {invalidCanned || !cannedResponse ? (
            <Flex gap={withVar(SpacingToken.Spacing1)}>
              <Icon size="small" source={Info} customColor={withVar(DesignToken.ContentBasicNegative)} />
              <Text size="sm" customColor={withVar(DesignToken.ContentBasicNegative)}>
                Select canned response
              </Text>
            </Flex>
          ) : (
            <CannedResponseContent cannedResponse={cannedResponse} fullWidth />
          )}
        </MessageContainer>
      );
    }

    const invalidMessage = !input.value.template.text;

    return (
      <MessageContainer error={invalidMessage} onClick={onAccordionOpen}>
        {invalidMessage ? (
          <>
            <Flex gap={withVar(SpacingToken.Spacing1)}>
              <Icon size="small" source={Info} customColor={withVar(DesignToken.ContentBasicNegative)} />
              <Text size="sm" customColor={withVar(DesignToken.ContentBasicNegative)}>
                Enter message…
              </Text>
            </Flex>
          </>
        ) : (
          <>{input.value.template?.text}</>
        )}
      </MessageContainer>
    );
  }

  return null;
};

const MessageContainer = styled.div<{ error?: boolean }>`
  cursor: pointer;
  margin-left: var(${SpacingToken.Spacing7});
  padding: var(${SpacingToken.Spacing3}) var(${SpacingToken.Spacing4});
  border-radius: var(${RadiusToken.Radius3});
  background-color: var(
    ${({ error }): string =>
      error ? DesignToken.SurfaceAccentEmphasisLowNegative : DesignToken.SurfaceTertiaryDefault}
  );
`;
